import {
  connectDigest,
  connectOrigin,
  randomConnectToken,
  requireConnect,
  type ConnectFetch,
} from "@afar-research/shared/connect";

// Account creation and email verification need more time than an ordinary sign-in.
export const connectSignInTimeoutMs = 15 * 60 * 1000;

/**
 * `offline_access` is what makes a saved account survive its access token. Without it the identity
 * service issues no refresh token, the credential on disk can never be renewed, and every
 * authenticated route starts refusing the user the moment the access token expires — while the app
 * still shows them as signed in (#1008).
 */
export const connectSignInScopes = ["openid", "profile", "email", "offline_access"] as const;

/**
 * What the identity service granted. `refreshToken` is absent when the service declined to issue
 * one, which is a credential that will expire and cannot be renewed rather than a failure: the
 * sign-in still worked, and saying so lets custody record what it actually holds.
 */
export interface ConnectTokens {
  readonly accessToken: string;
  readonly refreshToken?: string;
  /** Epoch milliseconds, absent when the service did not say. */
  readonly expiresAt?: number;
}

const readTokenResponse = (
  tokens: {
    access_token?: string;
    token_type?: string;
    refresh_token?: string;
    expires_in?: number;
  },
  now: number,
): ConnectTokens => {
  requireConnect(
    typeof tokens.access_token === "string" && tokens.token_type?.toLowerCase() === "bearer",
    "invalid_oauth_token",
    401,
  );
  return {
    accessToken: tokens.access_token!,
    ...(typeof tokens.refresh_token === "string" && tokens.refresh_token.length > 0
      ? { refreshToken: tokens.refresh_token }
      : {}),
    ...(typeof tokens.expires_in === "number" && Number.isFinite(tokens.expires_in)
      ? { expiresAt: now + tokens.expires_in * 1000 }
      : {}),
  };
};

/** Discover the identity endpoints used before opening the sign-in browser. */
export async function readConnectIdentity(input: { issuer: string; fetch: ConnectFetch }) {
  const issuer = connectOrigin(input.issuer);
  const metadataResponse = await input
    .fetch(
      new Request(`${issuer}/.well-known/openid-configuration`, {
        redirect: "error",
        signal: AbortSignal.timeout(10000),
      }),
    )
    .catch(() => undefined);
  requireConnect(metadataResponse?.ok, "identity_unavailable", 503);
  const metadata = (await metadataResponse!.json()) as {
    issuer: string;
    authorization_endpoint: string;
    token_endpoint: string;
  };
  requireConnect(metadata.issuer.replace(/\/$/, "") === issuer, "identity_issuer_mismatch");
  for (const endpoint of [metadata.authorization_endpoint, metadata.token_endpoint]) {
    requireConnect(new URL(endpoint).origin === issuer, "identity_endpoint_mismatch");
  }
  return metadata;
}

/** Public OAuth authorization-code flow with PKCE. UI owns browser/paste interaction. */
export async function signInConnect(input: {
  issuer: string;
  clientId: string;
  redirectUri: string;
  fetch: ConnectFetch;
  /** The platform's clock, for dating the grant's expiry. */
  now: () => Promise<number>;
  authorize: (url: string) => Promise<string>;
}) {
  const metadata = await readConnectIdentity(input);
  const verifier = randomConnectToken().replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  const state = randomConnectToken();
  const challenge = (await connectDigest(verifier))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  const authorization = new URL(metadata.authorization_endpoint);
  authorization.search = new URLSearchParams({
    client_id: input.clientId,
    redirect_uri: input.redirectUri,
    response_type: "code",
    scope: connectSignInScopes.join(" "),
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  }).toString();
  const callback = new URL(await input.authorize(authorization.href));
  const expectedCallback = new URL(input.redirectUri);
  requireConnect(
    callback.origin === expectedCallback.origin &&
      callback.pathname === expectedCallback.pathname &&
      callback.searchParams.get("state") === state &&
      callback.searchParams.getAll("code").length === 1 &&
      callback.searchParams.getAll("state").length === 1 &&
      !callback.searchParams.has("error"),
    "invalid_oauth_callback",
  );
  const code = callback.searchParams.get("code");
  requireConnect(code, "missing_oauth_code");
  const response = await input.fetch(
    new Request(metadata.token_endpoint, {
      method: "POST",
      redirect: "error",
      signal: AbortSignal.timeout(10000),
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: input.clientId,
        code,
        code_verifier: verifier,
        redirect_uri: input.redirectUri,
      }),
    }),
  );
  requireConnect(response.ok, "oauth_exchange_failed", 401);
  return readTokenResponse(await response.json(), await input.now());
}

/**
 * Renews an access token from the refresh token saved at sign-in.
 *
 * The two failures are kept apart because custody acts on them differently: a service that
 * refused the grant means the account must sign in again and the saved credential is spent, while
 * a service we could not reach means try later and keep what we have. Collapsing them throws away
 * a working credential over a network blip.
 */
export async function refreshConnectToken(input: {
  issuer: string;
  clientId: string;
  refreshToken: string;
  fetch: ConnectFetch;
  now: () => Promise<number>;
}): Promise<ConnectTokens> {
  const metadata = await readConnectIdentity(input);
  const response = await input
    .fetch(
      new Request(metadata.token_endpoint, {
        method: "POST",
        redirect: "error",
        signal: AbortSignal.timeout(10000),
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          client_id: input.clientId,
          refresh_token: input.refreshToken,
        }),
      }),
    )
    .catch(() => undefined);
  // No answer at all, or a server-side failure, is the service being unreachable. Only a refusal
  // it chose to make is a verdict on the grant.
  requireConnect(response, "identity_unavailable", 503);
  requireConnect(response.ok || response.status < 500, "identity_unavailable", 503);
  requireConnect(response.ok, "refresh_token_rejected", 401);
  return readTokenResponse(await response.json(), await input.now());
}
